import { LEAD_GENERATED_BY, leadGeneratedByDefault } from '../shared/leadAttribution';
import { recordOutreach } from './outreach';
import { syncPipelineContactApp } from './pipelineContactSync';
import { ref, push, set, update, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { newOwnership, indexOps, ownerOf } from './recordAccess';
import { createNotification } from './notificationService';
import { fullName, alertTarget, checkInDay } from './sellerInterestService';
import { syncConfirmTask } from './apptConfirm';
import { requestCalendarSync } from './googleSync';
import { syncPocketListing } from './pocketListingService';
import { startPipelineCheckIns, setPipelineCheckInsStopped } from './checkInService';

// Buyer Interests (in-app only, signed-in users). Stored under crm/buyerInterests/{id}, the same
// pattern as crm/sellerInterests: the existing crm/ rule covers it, so no new database rule is needed.
// Nothing here sends an email or text: alerts are in-app notifications and tasks only.

export const BI_PATH = 'crm/buyerInterests';
export const BI_STATUSES = ['Planning', 'Actively touring'];
export const BI_VISITING = 'Planning'; // optional "Coming to town" date
export const BI_TIMELINES = ['Now', '3 months', '6 months', 'Just curious'];
export const BI_FINANCING = ['Pre-approved', 'Cash', 'Not yet pre-approved'];
export const BI_PROPERTY_TYPES = ['Single family', 'Condo', 'Townhome', 'Villa', 'Multi-family', 'Vacant land', 'Other'];

export const EMPTY_BUYER = {
  maxAnnualFees: '', maxOneTimeFees: '', poolRequired: 'No',
  firstName: '', lastName: '', additionalContacts: [], phone: '', email: '',
  propertyType: '', beds: '', baths: '', mustHaves: '', priceMin: '', priceMax: '', areas: '',
  timeline: 'Now', financing: '', lender: '', alsoSelling: 'No', reasonForMoving: '', howHeard: '',
  consentText: false, consentEmail: false, assignedAgentId: '', appointmentAt: '', appointmentLocation: '', status: '', visitDate: '', notes: '',
};

const nowIso = () => new Date().toISOString();
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const digits = (s) => String(s || '').replace(/\D/g, '').slice(-10);

const addTask = async (agent, data, uid) => {
  const r = push(ref(database, `users/${agent}/tasks`));
  await set(r, {
    role: 'Agent', appliesTo: 'All', mode: 'task', status: 'pending', assignee: agent,
    createdAt: nowIso(), updatedAt: nowIso(), createdBy: uid || null, completedAt: null, notes: '', ...data,
  });
  return r.key;
};

// Creates or updates the matching Leads contact. Matches by contactId, then email, then phone (no duplicates).
// Links both ways: crm/buyerInterests/{id}/contactId and crm/contacts/{cid}/buyerInterestIds/{id} = true
export const syncBuyerContact = (interest, uid) => syncPipelineContactApp('buyer', BI_PATH, interest, uid);

// Showing/consult appointment -> task + crm/appointments entry. Planning with a coming-to-town date -> check-in task two weeks before (or on the date if sooner) on that date. Each fires once per new date.
const scheduleSideEffects = async (next, prev, uid, team) => {
  const out = {};
  const agent = alertTarget(next, team, uid);
  const who = fullName(next) || 'buyer';
  if (agent && next.appointmentAt && next.appointmentAt !== prev.apptTaskFor) {
    const d = new Date(next.appointmentAt);
    // Rescheduled: cancel the previous appointment so Google Calendar sync moves it instead of keeping a duplicate.
    if (prev.appointmentId) { try { await update(ref(database, `crm/appointments/${prev.appointmentId}`), { status: 'cancelled', cancelledAt: nowIso(), cancelledReason: 'rescheduled' }); } catch (e) { console.error('Could not cancel previous appointment', e); } }
    const a = push(ref(database, 'crm/appointments'));
    await set(a, { contactId: next.contactId || null, contactName: who, agentUid: agent, startAt: d.toISOString(), type: 'In person', location: next.appointmentLocation || '', notes: 'Showing / buyer consult (buyer interest)', status: 'scheduled', buyerInterestId: next.id, createdAt: nowIso(), createdBy: uid || null });
    out.apptTaskId = await addTask(agent, {
      name: `Buyer consult/showing: ${who}`, title: `Buyer consult/showing: ${who}`,
      description: `${next.areas ? `Areas: ${next.areas}. ` : ''}${next.phone ? `Phone: ${next.phone}` : ''}`,
      type: 'Appointment', source: 'buyer-interest-appointment', buyerInterestId: next.id, appointmentId: a.key,
      dueDate: isoDay(d), dueTime: hhmm(d), dueAt: d.toISOString(),
    }, uid);
    await recordOutreach(uid, 'appointment', 'buyer-interest', { contactId: next.contactId || undefined, contactName: who, apptType: 'Buyer consult', apptAt: d.toISOString() });
    requestCalendarSync();
    out.appointmentId = a.key; out.apptTaskFor = next.appointmentAt;
  }
  // Appointment date cleared: cancel the Resi appointment (Google Calendar sync then deletes its event) and its task.
  if (!next.appointmentAt && prev.appointmentId) {
    try {
      await update(ref(database, `crm/appointments/${prev.appointmentId}`), { status: 'cancelled', cancelledAt: nowIso(), cancelledReason: 'cleared' });
      if (prev.apptTaskId && agent) await update(ref(database, `users/${agent}/tasks/${prev.apptTaskId}`), { status: 'cancelled', notes: 'Appointment date cleared', updatedAt: nowIso() });
      requestCalendarSync();
    } catch (e) { console.error('Could not cancel cleared appointment', e); }
    out.appointmentId = null; out.apptTaskId = null; out.apptTaskFor = null;
  }
  try {
    Object.assign(out, await syncConfirmTask({ kind: 'buyer', id: next.id, firstName: next.firstName, name: who, phone: next.phone, address: next.appointmentLocation || '', apptAt: next.appointmentAt, agent, agentName: (team || []).find((t) => t.uid === agent)?.name || '' }, prev, uid));
  } catch (e) { console.error('Could not sync confirm task', e); }
  if (agent && next.status === BI_VISITING && next.visitDate && next.visitDate !== prev.visitTaskDate) {
    out.visitTaskId = await addTask(agent, {
      name: `Check in with ${who} before their visit`, title: `Check in with ${who} before their visit`,
      description: `Buyer interest: coming to town ${next.visitDate}.${next.areas ? ` Areas: ${next.areas}.` : ''}${next.phone ? ` Phone: ${next.phone}.` : ''}`,
      type: 'Follow-up', source: 'buyer-interest-visit', buyerInterestId: next.id, dueDate: checkInDay(next.visitDate),
    }, uid);
    out.visitTaskDate = next.visitDate; out.visitTaskOwner = agent;
  }
  return out;
};

// Pocket listing mirror (only when the agent turned "Show in pocket listings" on). Best-effort.
const pocketMirror = async (interest, uid) => { try { await syncPocketListing(interest, uid, 'buyer'); } catch (e) { console.error('Could not update pocket listing', e); } };

export const createBuyerInterest = async (data, uid, team) => {
  const r = push(ref(database, BI_PATH));
  const target = alertTarget(data, team, uid);
  const due = new Date(Date.now() + 60 * 60 * 1000);
  const label = fullName(data) || 'New buyer';
  const record = { ...EMPTY_BUYER, ...data, name: fullName(data), leadSource: (data.howHeard === 'Other' ? data.howHeardOther : data.howHeard) || 'Buyer interest', createdAt: nowIso(), updatedAt: nowIso(), createdBy: uid || null };
  // Private by default: owned by the assigned agent (an agent adding their own lead), else the creator.
  Object.assign(record, newOwnership(data.ownerUid || data.assignedAgentId || uid));
  // Lead generated by: agent when they enter their own lead, team when assigned by someone else or imported.
  record.leadGeneratedBy = LEAD_GENERATED_BY.includes(data.leadGeneratedBy) ? data.leadGeneratedBy : leadGeneratedByDefault({ creatorUid: uid, ownerUid: record.ownerUid, imported: !!data.imported });
  await update(ref(database), { [`${BI_PATH}/${r.key}`]: record, ...indexOps('buyerInterests', r.key, record) });
  let callTaskId = null;
  if (target) {
    callTaskId = await addTask(target, {
      name: `Call within the hour: ${label}`, title: `Call within the hour: ${label}`,
      description: `New buyer interest.${data.areas ? ` Areas: ${data.areas}.` : ''} Timeline: ${data.timeline || 'n/a'}.${data.phone ? ` Phone: ${data.phone}.` : ''}`,
      type: 'Follow-up', source: 'buyer-interest', buyerInterestId: r.key,
      dueDate: isoDay(due), dueTime: hhmm(due), dueAt: due.toISOString(),
    }, uid);
    try {
      await createNotification(target, {
        type: 'buyer_interest', title: 'New buyer interest',
        message: `${label}. Call within the hour.`, link: '/pipeline', buyerInterestId: r.key, channel: 'in-app',
      });
    } catch (e) { console.error('Could not create in-app alert', e); }
  }
  // Rolling check-ins: +48 hours, then +1 week, then every 2 weeks (see shared/checkIns.js).
  let checkInTaskId = null;
  try { checkInTaskId = await startPipelineCheckIns(r.key, record, target, uid); } catch (e) { console.error('Could not create the first check-in', e); }
  const contactId = await syncBuyerContact({ id: r.key, ...record }, uid);
  const extra = await scheduleSideEffects({ id: r.key, ...record, contactId }, {}, uid, team);
  await update(ref(database, `${BI_PATH}/${r.key}`), { alertTo: target || null, callTaskId, checkInTaskId, checkInsStopped: false, ...extra });
  await pocketMirror({ id: r.key, ...record }, uid);
  return r.key;
};

export const updateBuyerInterest = async (prev, patch, uid, team) => {
  const next = { ...prev, ...patch };
  if ('checkInsStopped' in patch && !!patch.checkInsStopped !== !!prev.checkInsStopped) {
    const ci = await setPipelineCheckInsStopped(prev, patch.checkInsStopped, uid);
    patch = { ...patch, ...ci };
  }
  const extra = await scheduleSideEffects(next, prev, uid, team);
  const out = { ...patch, ...extra, name: fullName(next), updatedAt: nowIso() };
  delete out.id; delete out.kind;
  delete out.ownerUid; delete out.sharedWith; // ownership changes only via Transfer / Share
  // An admin changing the assigned agent is a transfer: the new agent owns it, the previous owner leaves.
  if (patch.assignedAgentId && patch.assignedAgentId !== ownerOf(prev)) {
    const sharedWith = { ...(prev.sharedWith || {}) }; delete sharedWith[patch.assignedAgentId];
    out.ownerUid = patch.assignedAgentId; out.sharedWith = Object.keys(sharedWith).length ? sharedWith : null;
    const ops = Object.fromEntries(Object.entries(out).map(([k, v]) => [`${BI_PATH}/${prev.id}/${k}`, v === undefined ? null : v]));
    await update(ref(database), { ...ops, ...indexOps('buyerInterests', prev.id, { ownerUid: out.ownerUid, sharedWith }, prev) });
  } else await update(ref(database, `${BI_PATH}/${prev.id}`), out);
  await syncBuyerContact({ ...next, ...out, id: prev.id }, uid);
  await pocketMirror({ ...next, ...out, id: prev.id }, uid);
  return out;
};
